import { PipelineSeriesOnBuild, PipelineType } from '@nova/types';

import { maskRegionGlslVec3PropString } from '../glPipelines/components/regionMasks/regionMaskTypes';
import { ColourMatrixUniformData } from '../glPipelines/pipeline-colourMatrix';
import { pipelineSeriesParamConversion } from '../glPipelines/utils/pipeline-utils-types';

import { assertSameImageDimensions, cloneImageData, createBlankImageData } from './pixel-utils';

export type TintColour = readonly [number, number, number];

type MaskChannel = 'r' | 'g' | 'b';
type RgbaTuple = [number, number, number, number];
type RegionPixelEffect = (r: number, g: number, b: number, a: number) => RgbaTuple;

const INV_255 = 1 / 255;

function clamp255(value: number): number {
  return Math.min(255, Math.max(0, Math.round(value)));
}

function computeSafeNormTint(tr: number, tg: number, tb: number): number {
  const normTint = 0.30 * tr + 0.59 * tg + 0.11 * tb;
  return normTint === 0 ? 1 : normTint;
}

function applyTintRgba(
  r: number,
  g: number,
  b: number,
  a: number,
  tr: number,
  tg: number,
  tb: number,
  safeNorm: number
): RgbaTuple {
  return [
    clamp255((tr * r / safeNorm) * 255),
    clamp255((tg * g / safeNorm) * 255),
    clamp255((tb * b / safeNorm) * 255),
    Math.round(a * 255)
  ];
}

function applyColourMatrixRgba(
  r: number,
  g: number,
  b: number,
  a: number,
  matrix: Float32Array | number[],
  alpha: number
): RgbaTuple | null {
  if (alpha === 0) {
    return null;
  }

  let unpremultR = r;
  let unpremultG = g;
  let unpremultB = b;

  if (a > 0) {
    unpremultR /= a;
    unpremultG /= a;
    unpremultB /= a;
  }

  const m = matrix;
  const resultR = (m[0] * unpremultR) + (m[1] * unpremultG) + (m[2] * unpremultB) + (m[3] * a) + m[4];
  const resultG = (m[5] * unpremultR) + (m[6] * unpremultG) + (m[7] * unpremultB) + (m[8] * a) + m[9];
  const resultB = (m[10] * unpremultR) + (m[11] * unpremultG) + (m[12] * unpremultB) + (m[13] * a) + m[14];
  const resultA = (m[15] * unpremultR) + (m[16] * unpremultG) + (m[17] * unpremultB) + (m[18] * a) + m[19];

  const mixR = unpremultR + (resultR - unpremultR) * alpha;
  const mixG = unpremultG + (resultG - unpremultG) * alpha;
  const mixB = unpremultB + (resultB - unpremultB) * alpha;

  return [
    clamp255(mixR * resultA * 255),
    clamp255(mixG * resultA * 255),
    clamp255(mixB * resultA * 255),
    clamp255(resultA * 255)
  ];
}

function applyTintPixelsInPlace(image: ImageData, tintColour: TintColour | number[]): void {
  const [tr, tg, tb] = tintColour;
  const safeNorm = computeSafeNormTint(tr, tg, tb);
  const data = image.data;

  for (let i = 0; i < data.length; i += 4) {
    const [nr, ng, nb, na] = applyTintRgba(
      data[i] * INV_255,
      data[i + 1] * INV_255,
      data[i + 2] * INV_255,
      data[i + 3] * INV_255,
      tr,
      tg,
      tb,
      safeNorm
    );
    data[i] = nr;
    data[i + 1] = ng;
    data[i + 2] = nb;
    data[i + 3] = na;
  }
}

function applyColourMatrixPixelsInPlace(
  image: ImageData,
  matrix: Float32Array | number[],
  alpha: number
): void {
  const data = image.data;

  for (let i = 0; i < data.length; i += 4) {
    const result = applyColourMatrixRgba(
      data[i] * INV_255,
      data[i + 1] * INV_255,
      data[i + 2] * INV_255,
      data[i + 3] * INV_255,
      matrix,
      alpha
    );

    if (!result) {
      continue;
    }

    data[i] = result[0];
    data[i + 1] = result[1];
    data[i + 2] = result[2];
    data[i + 3] = result[3];
  }
}

function convertPipelineSeriesParams(entry: PipelineSeriesOnBuild[number]): unknown {
  const conversionFunc = pipelineSeriesParamConversion[entry.type];
  return conversionFunc ? conversionFunc(entry.params) : entry.params;
}

const pipelineSeriesInPlaceHandlers: Partial<Record<PipelineType, (image: ImageData, params: unknown) => void>> = {
  [PipelineType.tint]: (image, params) => applyTintPixelsInPlace(image, params as TintColour),
  [PipelineType.colourMatrix]: (image, params) => {
    const colourMatrixParams = params as ColourMatrixUniformData;
    applyColourMatrixPixelsInPlace(image, colourMatrixParams.matrix_4x5, colourMatrixParams.alpha);
  }
};

function getMaskChannelValue(data: Uint8ClampedArray, index: number, channel: MaskChannel): number {
  const channelOffset = channel === 'r' ? 0 : channel === 'g' ? 1 : 2;
  return data[index + channelOffset] * INV_255;
}

function resolveMaskChannel(colourName: string): MaskChannel {
  const channel = maskRegionGlslVec3PropString[colourName] as MaskChannel | undefined;
  if (!channel) {
    throw new Error(`Unknown region mask colour <${colourName}>`);
  }
  return channel;
}

/**
 * Applies a color tint to the image.
 */
export function applyTintPixels(image: ImageData, tintColour: TintColour | number[]): ImageData {
  const output = cloneImageData(image);
  applyTintPixelsInPlace(output, tintColour);
  return output;
}

/**
 * Applies a color tint to the image. Only pixels with no swatch mask channel active get the tint.
 */
export function applyColorToUnmaskedRegions(
  image: ImageData,
  mask: ImageData,
  tintColour: TintColour | number[],
  maskChannelThreshold = 1
): ImageData {
  assertSameImageDimensions(image, mask, 'applyColorToUnmaskedRegions');
  const output = cloneImageData(image);
  const [tr, tg, tb] = tintColour;
  const safeNorm = computeSafeNormTint(tr, tg, tb);
  const data = output.data;
  const maskData = mask.data;

  for (let i = 0; i < data.length; i += 4) {
    if (data[i + 3] === 0) {
      continue;
    }

    const isMasked = maskData[i] > maskChannelThreshold
      || maskData[i + 1] > maskChannelThreshold
      || maskData[i + 2] > maskChannelThreshold;

    if (isMasked) {
      continue;
    }

    const [nr, ng, nb, na] = applyTintRgba(
      data[i] * INV_255,
      data[i + 1] * INV_255,
      data[i + 2] * INV_255,
      data[i + 3] * INV_255,
      tr,
      tg,
      tb,
      safeNorm
    );
    data[i] = nr;
    data[i + 1] = ng;
    data[i + 2] = nb;
    data[i + 3] = na;
  }

  return output;
}

export function applyColourMatrixPixels(
  image: ImageData,
  matrix: Float32Array | number[],
  alpha: number
): ImageData {
  const output = cloneImageData(image);
  applyColourMatrixPixelsInPlace(output, matrix, alpha);
  return output;
}

export function applySummationPixels(imageA: ImageData, imageB: ImageData): ImageData {
  assertSameImageDimensions(imageA, imageB, 'applySummationPixels');
  const output = cloneImageData(imageA);
  const data = output.data;
  const otherData = imageB.data;

  for (let i = 0; i < data.length; i += 4) {
    data[i] = Math.min(255, data[i] + otherData[i]);
    data[i + 1] = Math.min(255, data[i + 1] + otherData[i + 1]);
    data[i + 2] = Math.min(255, data[i + 2] + otherData[i + 2]);
    data[i + 3] = Math.min(255, data[i + 3] + otherData[i + 3]);
  }

  return output;
}

export function applyMaskPixels(
  imageToMask: ImageData,
  imageAsMask: ImageData,
  invertMaskEffect: boolean
): ImageData {
  assertSameImageDimensions(imageToMask, imageAsMask, 'applyMaskPixels');
  const output = cloneImageData(imageToMask);
  const data = output.data;
  const maskData = imageAsMask.data;

  for (let i = 0; i < data.length; i += 4) {
    const mainAlpha = data[i + 3] * INV_255;
    const maskAlpha = maskData[i + 3] * INV_255;
    const alpha = invertMaskEffect
      ? mainAlpha * (1 - maskAlpha)
      : mainAlpha * maskAlpha;

    data[i] = Math.round((data[i] * INV_255) * alpha * 255);
    data[i + 1] = Math.round((data[i + 1] * INV_255) * alpha * 255);
    data[i + 2] = Math.round((data[i + 2] * INV_255) * alpha * 255);
    data[i + 3] = Math.round(alpha * 255);
  }

  return output;
}

export function applyOverlayPixels(imageBottom: ImageData, imageTop: ImageData): ImageData {
  assertSameImageDimensions(imageBottom, imageTop, 'applyOverlayPixels');
  const output = new ImageData(imageBottom.width, imageBottom.height);
  const bottomData = imageBottom.data;
  const topData = imageTop.data;
  const data = output.data;

  for (let i = 0; i < data.length; i += 4) {
    const bottomR = bottomData[i] * INV_255;
    const bottomG = bottomData[i + 1] * INV_255;
    const bottomB = bottomData[i + 2] * INV_255;
    const bottomA = bottomData[i + 3] * INV_255;

    const topR = topData[i] * INV_255;
    const topG = topData[i + 1] * INV_255;
    const topB = topData[i + 2] * INV_255;
    const topA = topData[i + 3] * INV_255;

    const alphaBotToUse = 1 - topA;

    data[i] = Math.min(255, Math.round((bottomR * alphaBotToUse + topR * topA) * 255));
    data[i + 1] = Math.min(255, Math.round((bottomG * alphaBotToUse + topG * topA) * 255));
    data[i + 2] = Math.min(255, Math.round((bottomB * alphaBotToUse + topB * topA) * 255));
    data[i + 3] = Math.min(255, Math.round((bottomA + topA) * 255));
  }

  return output;
}

function applyRegionMaskedEffect(
  regionBase: ImageData,
  mask: ImageData,
  colourName: string,
  scaleDownResult: boolean,
  effect: RegionPixelEffect
): ImageData {
  assertSameImageDimensions(regionBase, mask, 'applyRegionMaskedEffect');
  const channel = resolveMaskChannel(colourName);
  const output = createBlankImageData(regionBase.width, regionBase.height);
  const baseData = regionBase.data;
  const maskData = mask.data;
  const data = output.data;

  for (let i = 0; i < data.length; i += 4) {
    const maskChannel = getMaskChannelValue(maskData, i, channel);

    if (maskChannel === 0) {
      continue;
    }

    let [r, g, b, a] = effect(
      baseData[i] * INV_255,
      baseData[i + 1] * INV_255,
      baseData[i + 2] * INV_255,
      baseData[i + 3] * INV_255
    );

    if (scaleDownResult && maskChannel < 1) {
      r = Math.round(r * maskChannel);
      g = Math.round(g * maskChannel);
      b = Math.round(b * maskChannel);
      a = Math.round(a * maskChannel);
    }

    data[i] = r;
    data[i + 1] = g;
    data[i + 2] = b;
    data[i + 3] = a;
  }

  return output;
}

function applyRegionMaskedTint(
  regionBase: ImageData,
  mask: ImageData,
  colourName: string,
  tintColour: TintColour | number[],
  scaleDownResult: boolean
): ImageData {
  const [tr, tg, tb] = tintColour;
  const safeNorm = computeSafeNormTint(tr, tg, tb);

  return applyRegionMaskedEffect(regionBase, mask, colourName, scaleDownResult, (r, g, b, a) =>
    applyTintRgba(r, g, b, a, tr, tg, tb, safeNorm));
}

function applyRegionMaskedColourMatrix(
  regionBase: ImageData,
  mask: ImageData,
  colourName: string,
  matrix: Float32Array | number[],
  alpha: number,
  scaleDownResult: boolean
): ImageData {
  return applyRegionMaskedEffect(regionBase, mask, colourName, scaleDownResult, (r, g, b, a) => {
    const result = applyColourMatrixRgba(r, g, b, a, matrix, alpha);
    if (!result) {
      return [
        Math.round(r * 255),
        Math.round(g * 255),
        Math.round(b * 255),
        Math.round(a * 255)
      ];
    }
    return result;
  });
}

const regionPipelineStepHandlers: Partial<Record<PipelineType, (
  regionBase: ImageData,
  mask: ImageData,
  colourName: string,
  params: unknown,
  scaleDown: boolean
) => ImageData>> = {
  [PipelineType.tint]: (regionBase, mask, colourName, params, scaleDown) =>
    applyRegionMaskedTint(regionBase, mask, colourName, params as TintColour, scaleDown),
  [PipelineType.colourMatrix]: (regionBase, mask, colourName, params, scaleDown) => {
    const colourMatrixParams = params as ColourMatrixUniformData;
    return applyRegionMaskedColourMatrix(
      regionBase,
      mask,
      colourName,
      colourMatrixParams.matrix_4x5,
      colourMatrixParams.alpha,
      scaleDown
    );
  }
};

export function runPipelineSeriesOnImageData(
  base: ImageData,
  seriesBuilt: PipelineSeriesOnBuild
): ImageData {
  if (seriesBuilt.length === 0) {
    return cloneImageData(base);
  }

  const current = cloneImageData(base);

  for (const entry of seriesBuilt) {
    const params = convertPipelineSeriesParams(entry);
    const handler = pipelineSeriesInPlaceHandlers[entry.type];
    if (handler) {
      handler(current, params);
    }
  }

  return current;
}

export function runRegionPipelineSeriesOnImageData(
  base: ImageData,
  mask: ImageData,
  colourName: string,
  seriesBuilt: PipelineSeriesOnBuild,
  scaleDownFinalStep: boolean
): ImageData {
  assertSameImageDimensions(base, mask, 'runRegionPipelineSeriesOnImageData');

  if (seriesBuilt.length === 0) {
    return cloneImageData(base);
  }

  let regionBase: ImageData = base;
  let current: ImageData = createBlankImageData(base.width, base.height);

  for (let index = 0; index < seriesBuilt.length; index += 1) {
    const entry = seriesBuilt[index];
    const isLast = index === seriesBuilt.length - 1;
    const scaleDown = isLast && scaleDownFinalStep;
    const params = convertPipelineSeriesParams(entry);
    const handler = regionPipelineStepHandlers[entry.type];

    if (!handler) {
      continue;
    }

    current = handler(regionBase, mask, colourName, params, scaleDown);
    regionBase = current;
  }

  return current;
}
